import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Chip, CircularProgress, FormControlLabel, Stack, Switch, Tooltip,
  Typography,
} from '@mui/material';

import api from '../../../../services/api';
import NavegadorDeHosts from './NavegadorDeHosts';
import { CODIGO } from './estilo';

const ROTA = '/admin/snapshot';

// Fundo de cada tipo de linha. Verde e vermelho suaves, na mesma chave clara do
// resto — o que precisa gritar e a linha que mudou, nao a caixa.
const FUNDO = {
  igual: null,
  mudou: '#fffbeb',
  so_esquerda: '#fef2f2',
  so_direita: '#f0fdf4',
};

// A barra lateral carrega a cor forte; o fundo fica claro. Assim dezenas de
// linhas alteradas seguidas continuam legiveis.
const BARRA = {
  igual: 'transparent',
  mudou: '#fcd34d',
  so_esquerda: '#fca5a5',
  so_direita: '#86efac',
};

/**
 * Texto cru das duas coletas, lado a lado, com TODOS os hosts num scroll so.
 *
 * O alinhamento vem pronto do servidor, e e o que faz o painel duplo dizer a
 * verdade: sem alinhar, uma unica linha inserida de um lado desloca todo o resto
 * um degrau e o operador passa a comparar linhas que nao tem relacao nenhuma.
 *
 * Cada linha vem marcada com o host a que pertence, o que sustenta o cabecalho
 * fixo e os atalhos — rolando cem linhas, o nome do equipamento continua na
 * tela.
 */
/**
 * Uma metade da linha, no NIVEL DO MODULO.
 *
 * Estava declarada DENTRO do `.map()` das linhas: um tipo de componente novo por
 * linha, a cada render. O React compara tipos por identidade, entao nenhuma
 * linha era atualizada — todas eram desmontadas e remontadas. Numa saida de
 * `display bgp peer` com dezenas de peers isso e o DOM inteiro do painel sendo
 * recriado, e o ponteiro piscando quando o no sob ele desaparece.
 */
function Celula({ texto, tipo, fundoBloco }) {
  return (
    <Box
      sx={{
        flex: 1, minWidth: 0, px: 1, py: 0.1,
        bgcolor: FUNDO[tipo] || fundoBloco,
        borderLeft: `3px solid ${BARRA[tipo]}`,
        color: texto === null ? '#cbd5e1' : undefined,
        whiteSpace: 'pre', overflow: 'hidden', textOverflow: 'ellipsis',
      }}
    >
      {texto === null ? '·' : (texto || ' ')}
    </Box>
  );
}

export default function RawLadoALado({ antes, depois, empresa }) {
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [soDiferentes, setSoDiferentes] = useState(false);
  const caixa = useRef(null);

  useEffect(() => {
    if (!antes || !depois || antes === depois || !empresa) { setDados(null); return undefined; }
    let cancelado = false;
    setCarregando(true);

    api.get(`${ROTA}/run/sidebyside/${antes}/${depois}`, { params: { empresa } })
      .then((res) => { if (!cancelado) setDados(res.data?.data || null); })
      .catch(() => { if (!cancelado) setDados(null); })
      .finally(() => { if (!cancelado) setCarregando(false); });

    return () => { cancelado = true; };
  }, [antes, depois, empresa]);

  // Com o filtro ligado os indices mudam, então as seções são recalculadas por
  // cima do que está de fato na tela — senão os atalhos pulariam para o lugar
  // errado.
  const { linhas, secoes } = useMemo(() => {
    const todas = (dados?.lines || []).map((l, i) => ({ ...l, i }));
    if (!soDiferentes) {
      return { linhas: todas, secoes: dados?.sections || [] };
    }
    const filtradas = todas.filter((l) => l.t !== 'igual');
    const secs = [];
    let atual = null;
    filtradas.forEach((l, idx) => {
      if (l.h !== atual) {
        atual = l.h;
        const orig = (dados?.sections || []).find((s) => s.host_id === l.h);
        secs.push({
          host_id: l.h,
          nome: orig?.nome || `host ${l.h}`,
          inicio: idx,
          diferentes: orig?.diferentes || 0,
        });
      }
    });
    return { linhas: filtradas, secoes: secs };
  }, [dados, soDiferentes]);

  const ordemPorHost = useMemo(
    () => new Map(secoes.map((sec, i) => [sec.host_id, i])),
    [secoes]
  );

  const indicePorLinha = useMemo(() => {
    const m = {};
    secoes.forEach((s, i) => { m[s.inicio] = i; });
    return m;
  }, [secoes]);

  if (carregando) return <Box sx={{ py: 2 }}><CircularProgress size={20} /></Box>;
  if (!dados) return null;

  return (
    <Box>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
        <Chip
          size="small"
          label={dados.identical ? 'idêntico' : `${dados.different} linha(s) diferentes`}
          sx={{ height: 20, color: '#fff', bgcolor: dados.identical ? '#16a085' : '#d97706' }}
        />
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Esconde as linhas iguais nos dois lados">
          <FormControlLabel
            control={
              <Switch size="small" checked={soDiferentes}
                onChange={(e) => setSoDiferentes(e.target.checked)} />
            }
            label={<Typography variant="caption">só as diferentes</Typography>}
          />
        </Tooltip>
      </Stack>

      {dados.identical && (
        <Alert severity="success" sx={{ mb: 1.5 }}>
          O texto das duas coletas é <strong>byte a byte idêntico</strong> em todos
          os equipamentos.
        </Alert>
      )}

      <NavegadorDeHosts secoes={secoes} containerRef={caixa}>
        {(registrar, cabecalho) => (
          <Box ref={caixa} sx={{ ...CODIGO, p: 0, maxHeight: '58vh', overflow: 'auto' }}>
            {cabecalho}
            <Stack
              direction="row"
              sx={{
                position: 'sticky', top: 27, zIndex: 3,
                bgcolor: '#f1f5f9', color: '#64748b', fontWeight: 700, fontSize: 11,
                borderBottom: '1px solid #e2e8f0',
              }}
            >
              <Box sx={{ flex: 1, px: 1, py: 0.3 }}>antes</Box>
              <Box sx={{ width: '1px', bgcolor: '#cbd5e1' }} />
              <Box sx={{ flex: 1, px: 1, py: 0.3 }}>depois</Box>
            </Stack>

            {linhas.map((l, i) => {
              const inicioDeSecao = indicePorLinha[i] !== undefined;
              // Fundo alternado por host: marca a troca de equipamento mesmo
              // para quem rolou rápido e não viu o cabeçalho mudar.
              // `secoes.findIndex` aqui era uma varredura por LINHA: com 15 hosts
              // e mil linhas, quinze mil comparacoes a cada render.
              const ordem = ordemPorHost.get(l.h) ?? 0;
              const fundoBloco = ordem % 2 === 1 ? '#f8fafc' : 'transparent';

              return (
                <Stack
                  key={i}
                  direction="row"
                  ref={inicioDeSecao ? registrar(i) : undefined}
                  sx={inicioDeSecao ? { borderTop: '2px solid #cbd5e1' } : undefined}
                >
                  <Celula texto={l.e} tipo={l.t} fundoBloco={fundoBloco} />
                  <Box sx={{ width: '1px', bgcolor: '#e2e8f0' }} />
                  <Celula texto={l.d} tipo={l.t} fundoBloco={fundoBloco} />
                </Stack>
              );
            })}

            {!linhas.length && (
              <Typography variant="caption" sx={{ color: '#94a3b8', p: 1, display: 'block' }}>
                {soDiferentes ? 'Nenhuma linha diferente.' : 'Sem saída.'}
              </Typography>
            )}
          </Box>
        )}
      </NavegadorDeHosts>
    </Box>
  );
}
